import React from 'react';
import { Search, UserPlus } from 'lucide-react';
import './WorkyMainDemoOpsWorkforceScreen.css';

interface WorkforceWorker {
    readonly id: string;
    readonly name: string;
    readonly initials: string;
    readonly avatarBg: string;
    readonly role: string;
    readonly specialty: string;
    readonly availability: readonly string[];
    readonly rating: number;
    readonly isOnShift: boolean;
}

const WORKFORCE_WORKERS: readonly WorkforceWorker[] = [
    {
        id: 'sarah-mitchell',
        name: 'Sarah Mitchell',
        initials: 'SM',
        avatarBg: 'linear-gradient(135deg, #1d4ed8, #60a5fa)',
        role: 'RN',
        specialty: 'ICU · Float Pool',
        availability: ['Morning', 'Afternoon'],
        rating: 5.0,
        isOnShift: false,
    },
    {
        id: 'priya-patel',
        name: 'Priya Patel',
        initials: 'PP',
        avatarBg: 'linear-gradient(135deg, #6d28d9, #a78bfa)',
        role: 'PSW',
        specialty: 'Memory Care · LTC',
        availability: ['Afternoon'],
        rating: 4.8,
        isOnShift: true,
    },
    {
        id: 'james-okafor',
        name: 'James Okafor',
        initials: 'JO',
        avatarBg: 'linear-gradient(135deg, #065f46, #34d399)',
        role: 'LPN',
        specialty: 'Med-Surg · Nights',
        availability: ['Morning', 'Evening'],
        rating: 4.9,
        isOnShift: false,
    },
    {
        id: 'maria-santos',
        name: 'Maria Santos',
        initials: 'MS',
        avatarBg: 'linear-gradient(135deg, #9f1239, #fb7185)',
        role: 'RN',
        specialty: 'OR Scrub · PACU',
        availability: ['Morning', 'Afternoon'],
        rating: 5.0,
        isOnShift: true,
    },
    {
        id: 'david-chen',
        name: 'David Chen',
        initials: 'DC',
        avatarBg: 'linear-gradient(135deg, #0e7490, #22d3ee)',
        role: 'PT',
        specialty: 'Physiotherapy · Ortho',
        availability: ['Afternoon'],
        rating: 4.7,
        isOnShift: true,
    },
    {
        id: 'aisha-williams',
        name: 'Aisha Williams',
        initials: 'AW',
        avatarBg: 'linear-gradient(135deg, #92400e, #fbbf24)',
        role: 'RPN',
        specialty: 'Mental Health · Crisis',
        availability: ['Morning', 'Afternoon'],
        rating: 5.0,
        isOnShift: false,
    },
    {
        id: 'tom-brooks',
        name: 'Tom Brooks',
        initials: 'TB',
        avatarBg: 'linear-gradient(135deg, #4f46e5, #818cf8)',
        role: 'HCA',
        specialty: 'Geriatrics · LTC',
        availability: ['Evening'],
        rating: 4.6,
        isOnShift: false,
    },
    {
        id: 'nina-voronova',
        name: 'Nina Voronova',
        initials: 'NV',
        avatarBg: 'linear-gradient(135deg, #be185d, #f472b6)',
        role: 'RN',
        specialty: 'Paediatrics · ER',
        availability: ['Morning', 'Afternoon'],
        rating: 5.0,
        isOnShift: true,
    },
];

const WorkyMainDemoOpsWorkforceScreen = (): React.ReactElement => (
    <div className='wmw-container'>

        {/* Toolbar */}
        <div className='wmw-toolbar'>
            <div className='wmw-search'>
                <Search size={10} className='wmw-search-icon' />
                Search by Worker, Role, City...
            </div>
            <button className='wmw-toolbar-btn wmw-toolbar-btn--primary'>
                <UserPlus size={10} />
                Invite Worker
            </button>
        </div>

        {/* Stats strip */}
        <div className='wmw-stats'>
            <div className='wmw-stat-card wmw-stat-card--blue'>
                <span className='wmw-stat-label'>Total Workers</span>
                <span className='wmw-stat-value'>214</span>
                <span className='wmw-stat-sub'>↑ 12 this month</span>
            </div>
            <div className='wmw-stat-card wmw-stat-card--green'>
                <span className='wmw-stat-label'>Active</span>
                <span className='wmw-stat-value'>198</span>
                <span className='wmw-stat-sub'>92.5% utilisation</span>
            </div>
            <div className='wmw-stat-card wmw-stat-card--purple'>
                <span className='wmw-stat-label'>On Shift</span>
                <span className='wmw-stat-value'>47</span>
                <span className='wmw-stat-sub'>Right now</span>
            </div>
            <div className='wmw-stat-card wmw-stat-card--amber'>
                <span className='wmw-stat-label'>Avg. Rating</span>
                <span className='wmw-stat-value'>4.9</span>
                <span className='wmw-stat-sub'>across all workers</span>
            </div>
        </div>

        {/* Worker grid */}
        <div className='wmw-grid'>
            {WORKFORCE_WORKERS.map((worker: WorkforceWorker) => (
                <div
                    key={worker.id}
                    className='wmw-card'
                    {...(worker.id === 'priya-patel'
                        ? { 'data-demo-target': 'workforce-row-priya' }
                        : worker.id === 'david-chen'
                        ? { 'data-demo-target': 'workforce-row-david' }
                        : {})}
                >
                    <div className='wmw-card-top'>
                        <div className='wmw-avatar' style={{ background: worker.avatarBg }}>
                            {worker.initials}
                        </div>
                        <span className={`wmw-status ${worker.isOnShift ? 'wmw-status--on-shift' : 'wmw-status--available'}`}>
                            <span className='wmw-status-dot' />
                            {worker.isOnShift ? 'On Shift' : 'Hired'}
                        </span>
                    </div>

                    <div className='wmw-card-identity'>
                        <span className='wmw-worker-name'>{worker.name}</span>
                        <span className='wmw-role-tag'>{worker.role}</span>
                    </div>

                    <span className='wmw-worker-specialty'>{worker.specialty}</span>

                    <div className='wmw-card-footer'>
                        <div className='wmw-availability'>
                            {worker.availability.map((slot: string) => (
                                <span key={slot} className='wmw-avail-tag'>{slot}</span>
                            ))}
                        </div>
                        <span className='wmw-rating'>★ {worker.rating.toFixed(1)}</span>
                    </div>
                </div>
            ))}
        </div>

    </div>
);

export { WorkyMainDemoOpsWorkforceScreen };
